import React, { useState } from "react";
import { z } from "zod";

import { SingleEmployeeSchema } from "../../../../components/EmployeePicker/utils";
import WorkspaceFormModal from "./WorkspaceFormModal";

import { useCreateMeetingWorkspace, useCurrentUser } from "../../../../api/hooks";
import useForm from "../../../../hooks/useForm";

type CreateWorkspaceFormProps = {
	isOpen: boolean;
	handleClose: () => void;
};

const CreateWorkspaceForm = ({ isOpen, handleClose }: CreateWorkspaceFormProps) => {
	// Current user will always be a participant
	const { data: currentUser } = useCurrentUser();
	const [error, setError] = useState<Record<string, { message: string }>>({});

	// Workspace form state
	const { validate, resetForm, updateForm, state } = useForm({
		schema: z.object({
			name: z
				.string()
				.min(1, { message: "This is a required field" })
				.max(280, { message: "Name cannot exceed 280 characters" }),
			participant: SingleEmployeeSchema.nullable().refine((data) => data && data?.value !== "", {
				message: "This is a required field",
			}),
		}),
		defaultValues: {
			name: "",
			participant: null,
		},
	});

	// Create workspace request
	const { mutate: createWorkspace, isPending } = useCreateMeetingWorkspace({
		onSuccess: () => {
			onClose();
		},
	});

	const onSubmit = () => {
		if (!currentUser?.me?.authorization?.payload.user_id) {
			return;
		}

		// Validate the form
		const validationResult = validate();
		if (!validationResult.isValid) {
			setError(validationResult.errors);
			return;
		}

		// Create the workspace
		createWorkspace({
			name: state.name,
			participants: [
				{ role: "editor", userId: currentUser?.me?.authorization?.payload.user_id },
				{ role: "editor", userId: state.participant?.value ?? "" },
			],
		});
	};

	// Reset form and close modal
	const onClose = () => {
		resetForm();
		setError({});
		handleClose();
	};

	return (
		<WorkspaceFormModal
			handleSubmit={onSubmit}
			handleClose={onClose}
			strings={{
				title: "Create a New Meeting Workspace",
				cancel: "Cancel",
				submit: "Create Workspace",
			}}
			state={{
				...state,
				isOpen,
				isPending,
				isParticipantEditable: true,
				error,
			}}
			updateForm={updateForm}
		/>
	);
};

export default CreateWorkspaceForm;
